import { enableProdMode, APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { RouteReuseStrategy, provideRouter } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { defineCustomElements } from '@ionic/pwa-elements/loader';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';
import packageInfo from '../package.json';

import { AuthService } from './app/core/services/auth.service';
import { ContactService } from './app/core/services/contact.service';
import { JwtInterceptor } from './app/_interceptors/jwt.interceptors';
import { AuthGuard } from './app/_guards/auth.guard';
import { AppUpdateService } from './app/services/app-update.service';
import { UntypedFormBuilder } from '@angular/forms';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { IonicStorageModule } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { LanguageService } from './app/core/services/language.service';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular(),
    provideRouter(routes),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    // Register the Angular service worker for the installable PWA.
    // Enabled only on a production *web* build — never inside the native
    // Capacitor app, where a service worker can interfere with the webview.
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production && !Capacitor.isNativePlatform(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideMessaging(() => getMessaging()),
    importProvidersFrom(
      IonicStorageModule.forRoot({
        driverOrder: [
          Drivers.IndexedDB,
          Drivers.LocalStorage
        ]
      })
    ),
    provideTranslateService({
      defaultLanguage: 'en',
    }),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json'
    }),
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: (authService: AuthService) => () => authService.loadToken(),
      deps: [AuthService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (languageService: LanguageService) => () => languageService.initializeLanguage(),
      deps: [LanguageService],
      multi: true,
    },
    AuthService,
    ContactService,
    AuthGuard,
    AppUpdateService,
    UntypedFormBuilder,
    FileOpener,
    {
      provide: APP_INITIALIZER,
      useFactory: (library: FaIconLibrary) => () => {
        library.addIconPacks(fas, fab, far);
      },
      deps: [FaIconLibrary],
      multi: true,
    },
  ],
}).catch((err) => console.error(err));

// Enable Ionic PWA Elements (camera, action sheet, etc.) when running on the web.
defineCustomElements(window);
